import type { JSX } from "react";
import clsx from "clsx";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type { EditorHintRegistry, HintVariant } from "@/lib/webHints/types";
import { fieldHintKind, fieldVisible } from "@/lib/webHints/variants";

/**
 * Přeloží klíč nápovědy z registru (`hintKey`, `variantHintKeys`, účel záložky `{ backoffice }`).
 *
 * Typované `t()` adminu bere jen literální klíče slovníku; klíč z registru je ale data (`string`),
 * ne literál, a sjednocení všech klíčů `webHints.*` tu není. Proto jediné přetypování v adminu
 * právě tady, nikde jinde: existenci každého klíče registru v cs i en jako neprázdného řetězce
 * hlídá měřidlo nápověd (`scripts/kontrola/admin-napovedy.mjs`, pravidlo `text-chybi`, AK3),
 * takže chybějící klíč padne v řetězu `pnpm kontrola`, ne až v rozhraní.
 */
export function webHintText(t: TFunction, key: string, options?: Record<string, unknown>): string {
  return (t as unknown as (key: string, options?: Record<string, unknown>) => string)(key, options);
}

/**
 * Nápověda pole editoru: kde se hodnota ukáže na webu („Na webu: <stránka> → <sekce>“), nebo
 * k čemu slouží v back office (řez 10, F9.6 bod 1). Editor ji vykreslí pod polem, skupinou polí
 * nebo kartou mimo formulář s `field` jako řetězcovým literálem (klíč registru; u karty mimo
 * formulář klíč karty).
 *
 * Vrací `null`, když registr `field` nemá (nález měřidla AK2, za běhu nic nepadá) nebo když pole
 * ve variantě není (`fieldVisible`, `showIn`). Text záznamu předponu nenese, přidá ji komponenta
 * podle druhu pole ve variantě (`fieldHintKind`: `variantKind`, jinak druh záznamu); varianta může
 * mít vlastní text (`variantHintKeys`).
 */
export default function FieldHint({
  registry,
  field,
  variant,
  className,
}: {
  registry: EditorHintRegistry;
  field: string;
  variant: HintVariant;
  className?: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  const record = registry.fields[field];
  const kind = fieldHintKind(registry, field, variant);
  if (!record || !kind || !fieldVisible(registry, field, variant)) return null;

  const text = webHintText(t, record.variantHintKeys?.[variant] ?? record.hintKey);
  const prefix =
    kind === "web" ? t("webHints.common.webPrefix") : t("webHints.common.backofficePrefix");

  return (
    <p
      data-field-hint={kind}
      data-field={field}
      className={clsx("mt-1 text-xs text-gray-500 dark:text-gray-400", className)}
    >
      {`${prefix} ${text}`}
    </p>
  );
}
